/**
 * La version précachée qui **attend** l'accord de l'agent (#224).
 *
 * `registerType: 'prompt'` ne substitue jamais la nouvelle version d'autorité — une équipe en
 * pleine saisie ne doit pas voir l'app se recharger sous ses doigts. Elle reste donc en
 * `waiting` jusqu'à ce que quelqu'un appelle `applyUpdate()`, et **personne ne le pouvait** :
 * la fonction que Workbox rend vivait dans `service-worker.ts`, hors de portée de tout écran.
 * Un déploiement n'atteignait alors jamais un onglet ouvert, rechargement compris — le Service
 * Worker contrôlant la page, elle se resservait de son précache.
 *
 * Ce module est le lieu où cette fonction est retenue entre l'annonce et l'accord. Il ne
 * connaît ni Workbox ni React, et c'est le point : `service-worker.ts` importe
 * `virtual:pwa-register`, que seul le build de production résout — un écran qui remonterait
 * jusque-là ne serait plus montable en test.
 */

/** Ce que le Service Worker offre quand une version attend : la fonction qui l'applique. */
export type ApplyUpdate = () => void

export interface PendingUpdate {
  /** Appelé par `onNeedRefresh` : une version vient d'être précachée. */
  announce: (apply: ApplyUpdate) => void
  /** Les deux moitiés de `useSyncExternalStore` — l'invite vit hors de React. */
  subscribe: (listener: () => void) => () => void
  read: () => ApplyUpdate | null
}

export function createPendingUpdate(): PendingUpdate {
  let apply: ApplyUpdate | null = null
  const listeners = new Set<() => void>()

  return {
    announce: (next) => {
      apply = next
      listeners.forEach((listener) => listener())
    },
    subscribe: (listener) => {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
    read: () => apply,
  }
}
